import clsx from 'clsx';

import { type GenericConfig } from '~/types';
import { type DynamicPageSectionName, type SectionMap } from '~/types/template';

import { getDynamicClasses, showPageSections } from '../../utils';
import { renderComponents } from '../../utils/renderComponents';
import { Section } from '../Section/Section';

import styles from './DynamicPageTemplate.module.css';

interface DynamicPageTemplateProps {
  genericConfig: GenericConfig;
  hasBannerAds: boolean;
  sections: Pick<SectionMap, DynamicPageSectionName>;
}

export const DynamicPageTemplate = ({ genericConfig, hasBannerAds, sections }: DynamicPageTemplateProps) => {
  const { showBottom, showCenterBottom, showCenterTop, showRight, showTop } = showPageSections(sections);

  const { bottom, centerBottom, centerTop, contentContainer, mainWrapper, right, top } = getDynamicClasses(sections);

  const containerClasses = clsx('adverts-and-atlas-wrapper', {
    'adverts-and-atlas-wrapper--adless': !hasBannerAds,
  });

  return (
    <div className={containerClasses}>
      <main className={`${styles.mainWrapper} ${mainWrapper}`} id="dynamic-page-main-tag-wrapper">
        {hasBannerAds && (
          <div className="ad-left-container">
            <div id="ad-left"></div>
          </div>
        )}
        <Section
          ariaLabel="Page Header Top Section"
          className={`${styles.dynamicPageTop} ${top}`}
          content={renderComponents({
            components: sections.top ?? null,
            genericConfig,
          })}
          dataTestId="dynamic-page-top"
          id="dynamic-page-top"
          show={showTop}
        />
        <div
          className={`${styles.dynamicPageContentContainer} ${showRight ? styles.standardWithRight : styles.standard} ${contentContainer}`}
          id="dynamic-page-content-container"
        >
          <>
            <Section
              ariaLabel="Main Content Center Top section"
              className={`${styles.dynamicPageCenterTop} ${centerTop}`}
              content={renderComponents({
                components: sections.centerTop ?? null,
                genericConfig,
              })}
              dataTestId="dynamic-page-center-top"
              id="dynamic-page-center-top"
              show={showCenterTop}
            />
          </>
          <>
            <Section
              ariaLabel="Secondary Content Right Section"
              className={`${styles.dynamicPageRight} ${right}`}
              content={renderComponents({
                components: sections.right ?? null,
                genericConfig,
              })}
              dataTestId="dynamic-page-right"
              id="dynamic-page-right"
              show={showRight}
            />
          </>
          <Section
            ariaLabel="Main Content Center Bottom Section"
            className={`${styles.dynamicPageCenterBottom} ${centerBottom}`}
            content={renderComponents({
              components: sections.centerBottom ?? null,
              genericConfig,
            })}
            dataTestId="dynamic-page-center-bottom"
            id="dynamic-page-center-bottom"
            show={showCenterBottom}
          />
        </div>
        <Section
          ariaLabel="Informational Content Bottom Section"
          className={`${styles.dynamicPageBottom} ${bottom}`}
          content={renderComponents({
            components: sections.bottom ?? null,
            genericConfig,
          })}
          dataTestId="dynamic-page-bottom"
          id="dynamic-page-bottom"
          show={showBottom}
        />
        {hasBannerAds && (
          <div className="ad-right-container">
            <div id="ad-right"></div>
          </div>
        )}
      </main>
    </div>
  );
};
